
import React from 'react';

const AnnualReportPage: React.FC = () => {
  const reports = [
    {
      year: '2023',
      title: 'Building Bridges, Preserving Heritage',
      description: 'A year of growth, community engagement, and cultural preservation',
      highlights: ['50+ cultural events', '200+ students in Farsi School', '$150K raised for programs'],
      downloadUrl: '#',
      isLatest: true
    },
    {
      year: '2022',
      title: 'Resilience and Renewal',
      description: 'Adapting to challenges while maintaining our cultural mission',
      highlights: ['35+ virtual and in-person events', '180+ students', '$120K in community support'],
      downloadUrl: '#',
      isLatest: false
    },
    {
      year: '2021',
      title: 'Innovation in Tradition',
      description: 'Embracing digital platforms to reach our community',
      highlights: ['40+ online events', '160+ remote learners', '$100K in donations'],
      downloadUrl: '#',
      isLatest: false
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-red-600 to-orange-600 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Annual Reports</h1>
            <p className="text-xl md:text-2xl text-red-100 max-w-3xl mx-auto">
              Transparency and accountability in our mission to preserve Persian culture
            </p>
          </div>
        </div>
      </section>

      {/* Reports Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Impact Over the Years</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Each year, we share our achievements, challenges, and financial transparency with our community
            </p>
          </div>

          <div className="space-y-8">
            {reports.map((report) => (
              <div key={report.year} className={`bg-white rounded-xl shadow-lg border-2 p-8 ${report.isLatest ? 'border-red-200 bg-red-50' : 'border-gray-200'}`}>
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                  <div className="flex-1">
                    <div className="flex items-center mb-4">
                      <span className="text-3xl font-bold text-red-600 mr-4">{report.year}</span>
                      {report.isLatest && (
                        <span className="bg-red-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                          Latest Report
                        </span>
                      )}
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{report.title}</h3>
                    <p className="text-gray-600 mb-6">{report.description}</p>
                    
                    <div className="grid md:grid-cols-3 gap-4 mb-6">
                      {report.highlights.map((highlight, index) => (
                        <div key={index} className="flex items-center space-x-2">
                          <i className="ri-check-line text-green-600"></i>
                          <span className="text-gray-700 text-sm">{highlight}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div className="lg:ml-8">
                    <a
                      href={report.downloadUrl}
                      className="inline-flex items-center bg-red-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-red-700 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-download-line mr-2"></i>
                      Download PDF
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Financial Transparency */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Financial Transparency</h2>
            <p className="text-lg text-gray-600">
              We believe in complete transparency about how your donations are used
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-xl shadow-sm text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-book-line text-blue-600 text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Programs</h3>
              <p className="text-3xl font-bold text-blue-600 mb-2">65%</p>
              <p className="text-gray-600 text-sm">Educational and cultural programs</p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-building-line text-green-600 text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Operations</h3>
              <p className="text-3xl font-bold text-green-600 mb-2">25%</p>
              <p className="text-gray-600 text-sm">Facility maintenance and utilities</p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-settings-line text-purple-600 text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Administration</h3>
              <p className="text-3xl font-bold text-purple-600 mb-2">10%</p>
              <p className="text-gray-600 text-sm">Administrative and governance costs</p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-red-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Questions About Our Reports?</h2>
          <p className="text-xl text-red-100 mb-8">
            We're committed to transparency and welcome any questions about our financial practices
          </p>
          <a
            href="/about/contact"
            className="bg-white text-red-600 px-8 py-3 rounded-lg font-medium hover:bg-red-50 transition-colors whitespace-nowrap cursor-pointer"
          >
            Contact Our Board
          </a>
        </div>
      </section>
    </div>
  );
};

export default AnnualReportPage;